/**
 * SupabaseWrapper - Authentification Supabase-only
 *
 * Flux d'authentification :
 * 1. Supabase Auth : email/password (admin web + techniciens mobile)
 * 2. Session existante : session Supabase en cours (refresh auto)
 *
 * Le token actif est toujours stocke dans localStorage('sso_token')
 * pour compatibilite avec toutes les pages existantes (le nom est
 * historique, ce n'est plus un token SSO).
 */

import React, { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import { createClient, Session } from '@supabase/supabase-js';
import ChangePasswordRequiredPage from './pages/ChangePasswordRequiredPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import SecondFacteurPage from './pages/SecondFacteurPage';
import SetPasswordPage from './pages/SetPasswordPage';
import { useViewport } from './hooks/useViewport';
import { Scope, scopeIsAtLeast } from './shared/permissions';
import { lireRefusTechnicien } from './utils/refusTechnicien';
import type { RefusTechnicien } from './utils/refusTechnicien';

/**
 * Decode le payload d'un JWT (sans validation crypto).
 * Necessaire pour recuperer les claims custom injectes par le Custom
 * Access Token Hook Supabase (notamment app_metadata.permissions).
 * supabase.auth.getUser() retourne un app_metadata depuis auth.users
 * (table) qui ne contient PAS les claims du hook a l'emission.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    // base64url -> base64 standard
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

// La cible est celle du build, et rien d'autre. Le 23/09/2026, un paquet construit sans
// .env est retombé sur des valeurs de repli codées ici — l'ANCIEN projet — et le site a
// authentifié ses utilisateurs dans l'ancienne base toute une journée de bascule, sous un
// bandeau qui annonçait la nouvelle. vite.config.ts refuse déjà de construire sans ces
// deux variables ; ceci est la seconde barrière, pour un paquet venu par un autre chemin.
const URL_CONSTRUITE = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const CLE_CONSTRUITE = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
if (!URL_CONSTRUITE || !CLE_CONSTRUITE) {
  throw new Error('VITE_SUPABASE_URL et VITE_SUPABASE_PUBLISHABLE_KEY sont requis : ce paquet a été construit sans .env');
}
// Typées « string » après la garde : TypeScript ne reporte pas le rétrécissement dans
// les fonctions déclarées plus bas (creerClientVerification).
const SUPABASE_URL: string = URL_CONSTRUITE;
const SUPABASE_PUBLISHABLE_KEY: string = CLE_CONSTRUITE;

// Client Supabase
export const supabase = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);

// Client jetable, sans persistance ni rafraichissement : prouver un mot de passe sans
// remplacer la session de l'application (une session aal2 redeviendrait aal1).
export function creerClientVerification() {
  return createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false, storageKey: 'servup-verification-mdp' }
  });
}

// Types
export interface SavUser {
  id: string;
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  dossier: string;
  role: string;
  account_id?: string;
  organization_id?: string;
  establishment_id?: string;
  deployment_type?: 'hosted' | 'dedicated';
  must_change_password?: boolean;
  authSource: 'supabase';
  /**
   * Permissions resolues depuis app_metadata.permissions du JWT
   * (peuplees par le Custom Access Token Hook cote Supabase).
   * Forme: { 'interventions.view': 'organization', 'pieces.view': 'global', ... }
   */
  permissions?: Record<string, Scope>;
}

interface AuthState {
  loading: boolean;
  authenticated: boolean;
  user: SavUser | null;
  error: string | null;
  showLogin: boolean;
}

/** Ecran de double authentification impose par le serveur (/api/auth/me -> mfa). */
interface EtatSecondFacteur {
  ecran: 'defi' | 'enrolement';
  facteurId?: string;
}

interface AuthContextValue {
  user: SavUser | null;
  logout: () => void;
  supabase: typeof supabase;
}

// Labels des roles seedes (super_admin, account_admin, org_admin,
// estab_admin, technician). Utilise pour l'affichage dans la TopBar et
// les pages Admins/Users. Pour les roles custom crees via /api/roles, le
// libelle vient de role.display_name (fetch direct).
// TODO : fetcher dynamiquement la map nom -> display_name depuis /api/roles
// au login pour supporter les roles custom partout.
export const ROLE_LABELS: Record<string, string> = {
  'super_admin': 'Super Admin',
  'account_admin': 'Admin Compte',
  'org_admin': 'Admin Organisation',
  'estab_admin': 'Admin Établissement',
  'technician': 'Technicien'
};

// Contexte pour partager les donnees utilisateur
const AuthContext = createContext<AuthContextValue | null>(null);

// Hook personnalise pour acceder au contexte
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit etre utilise a l\'interieur de SupabaseWrapper');
  }
  return context;
};

// Hook pour acceder aux donnees utilisateur
export const useUser = (): SavUser | null => {
  const { user } = useAuth();
  return user;
};

/**
 * Hook RBAC : verifie si l'utilisateur a une permission au scope minimum requis.
 * Lit user.permissions issu du Custom Access Token Hook Supabase.
 *
 * @example
 *   const canArchive = useHasPermission('interventions.archive');
 *   const canManageOrg = useHasPermission('users.update', 'organization');
 */
export const useHasPermission = (perm: string, minScope: Scope = 'establishment'): boolean => {
  const { user } = useAuth();
  const granted = user?.permissions?.[perm];
  if (!granted) return false;
  return scopeIsAtLeast(granted, minScope);
};

/**
 * Hook RBAC : retourne le scope accorde pour une permission, ou null si absente.
 * Utile pour conditionner l'UI selon le perimetre effectif (ex: switcher
 * d'etablissement visible si interventions.view est >= organization).
 */
export const usePermissionScope = (perm: string): Scope | null => {
  const { user } = useAuth();
  return (user?.permissions?.[perm] as Scope) || null;
};

// Hook pour acceder au token actif
export const useToken = (): string | null => {
  const [token, setToken] = React.useState<string | null>(null);

  React.useEffect(() => {
    const storedToken = localStorage.getItem('sso_token');
    setToken(storedToken);
  }, []);

  return token;
};

interface SupabaseWrapperProps {
  children: ReactNode;
}

function SupabaseWrapper({ children }: SupabaseWrapperProps) {
  const { isPhone } = useViewport();
  const [authState, setAuthState] = useState<AuthState>({
    loading: true,
    authenticated: false,
    user: null,
    error: null,
    showLogin: false
  });

  // State du formulaire login
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const loggingOut = useRef(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [secondFacteur, setSecondFacteur] = useState<EtatSecondFacteur | null>(null);
  // Un compte TECHNICIEN n'a pas accès au site : le serveur le refuse et joint son message et
  // le lien de l'application. Posé = l'écran « Espace réservé aux administrateurs » remplace
  // l'écran de connexion jusqu'à « Retour à la connexion ».
  const [refusTechnicien, setRefusTechnicien] = useState<RefusTechnicien | null>(null);
  // L1bis.7 — le lien d'invitation « #set_password_token=… » ouvre l'écran
  // « Définir mon mot de passe » AVANT toute session : lu une fois, jamais stocké.
  // Relecture L1bis : le jeton voyage dans le FRAGMENT (jamais envoyé au serveur) ;
  // la forme « ?set_password_token= » des premiers liens reste lue.
  const [setPasswordToken, setSetPasswordToken] = useState<string>(
    () => new URLSearchParams(window.location.hash.replace(/^#/, '')).get('set_password_token')
      || new URLSearchParams(window.location.search).get('set_password_token')
      || ''
  );

  // Ref pour eviter double-execution avec React StrictMode
  const initStarted = React.useRef(false);

  useEffect(() => {
    if (initStarted.current) {
      console.log('[Auth] initAuth() deja en cours, skip');
      return;
    }
    initStarted.current = true;
    initAuth();

    // Ecouter les changements de session Supabase
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Auth] Supabase auth state change:', event);
      if (event === 'SIGNED_IN' && session) {
        void handleSupabaseSession(session);
      } else if (event === 'SIGNED_OUT') {
        handleLogout();
      } else if (event === 'TOKEN_REFRESHED' && session) {
        // Mettre a jour le token en localStorage pour les pages
        localStorage.setItem('sso_token', session.access_token);
        // Relire les permissions depuis le nouveau JWT (le hook a pu les changer)
        void handleSupabaseSession(session);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Subscription Realtime aux signaux de refresh de permissions.
  // Quand un super_admin modifie un role / les permissions, on insere un row
  // dans auth_refresh_signals pour chaque user impacte. La subscription
  // postgres_changes detecte l'INSERT et declenche un refreshSession() qui
  // recalcule les permissions via le Custom Access Token Hook.
  useEffect(() => {
    const userId = authState.user?.id;
    if (!userId) return;

    console.log('[Auth] Subscription auth_refresh_signals pour user', userId);

    const channel = supabase
      .channel(`refresh-signal-${userId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'auth_refresh_signals',
        filter: `user_id=eq.${userId}`
      }, async (payload) => {
        const reason = (payload.new as { reason?: string } | null)?.reason || 'unknown';
        console.log('[Auth] Refresh signal recu, reason:', reason);
        const { data, error } = await supabase.auth.refreshSession();
        if (error) {
          console.error('[Auth] refreshSession() echoue:', error.message);
          return;
        }
        if (data.session) {
          await handleSupabaseSession(data.session);
        }
      })
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [authState.user?.id]);

  /**
   * Initialise l'authentification
   */
  async function initAuth() {
    try {
      console.log('[Auth] initAuth() appele');

      // Verifier session Supabase existante
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        console.log('[Auth] Session Supabase existante');
        // Recuperer les user_metadata fraiches du serveur (le JWT cache peut etre obsolete)
        const { data: { user: freshUser } } = await supabase.auth.getUser();
        if (freshUser) {
          session.user = freshUser;
        }
        await handleSupabaseSession(session);
        return;
      }

      // Aucune session — afficher formulaire de login
      console.log('[Auth] Aucune session — affichage formulaire login');
      setAuthState({
        loading: false,
        authenticated: false,
        user: null,
        error: null,
        showLogin: true
      });

    } catch (error) {
      console.error('[Auth] Erreur initialisation:', error);
      setAuthState({
        loading: false,
        authenticated: false,
        user: null,
        error: error instanceof Error ? error.message : 'Erreur d\'authentification',
        showLogin: true
      });
    }
  }

  /**
   * Extrait les infos utilisateur depuis une session Supabase
   */
  async function handleSupabaseSession(session: Session) {
    const user = session.user;

    // app_metadata depuis user.app_metadata ne contient PAS les claims injectes
    // par le Custom Access Token Hook (notamment permissions). On decode le
    // JWT directement pour les recuperer, avec fallback sur user.app_metadata
    // pour les champs standards.
    const decoded = decodeJwtPayload(session.access_token);
    const decodedAppMeta = (decoded?.app_metadata as Record<string, unknown>) || {};
    const appMeta: Record<string, unknown> = { ...(user.app_metadata || {}), ...decodedAppMeta };

    // deployment_type vient des app_metadata (pose par les Edge Functions a la creation)
    const userMeta = user.user_metadata || {};
    const firstName = userMeta.first_name || undefined;
    const lastName = userMeta.last_name || undefined;
    const displayName = firstName && lastName
      ? `${firstName} ${lastName}`
      : firstName || lastName || undefined;

    // Stocker le token tout de suite (necessaire pour les requetes RLS suivantes)
    localStorage.setItem('sso_token', session.access_token);

    // Realtime garde le jeton de la connexion : supabase-js ne le remplace que sur
    // SIGNED_IN / TOKEN_REFRESHED, PAS sur MFA_CHALLENGE_VERIFIED. Sans ce relais, un
    // canal ouvert en aal1 resterait filtre par les politiques <table>_aal2 apres le
    // code (comme chez Pimer, AuthProvider.jsx).
    try {
      supabase.realtime.setAuth(session.access_token);
    } catch (err) {
      console.warn('[Auth] realtime.setAuth a echoue:', err);
    }

    // Lire profiles.must_change_password pour declencher l'ecran force si besoin
    let mustChangePassword = false;
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('must_change_password')
        .eq('auth_user_id', user.id)
        .maybeSingle();
      if (profileError) {
        console.warn('[Auth] Lecture profiles.must_change_password impossible:', profileError.message);
      } else if (profile?.must_change_password === true) {
        mustChangePassword = true;
      }
    } catch (err) {
      console.warn('[Auth] Erreur lecture profil:', err);
    }

    const savUser: SavUser = {
      id: user.id,
      username: user.email || user.id,
      email: user.email || undefined,
      first_name: firstName,
      last_name: lastName,
      display_name: displayName,
      dossier: (appMeta.dossier as string) || '',
      role: (appMeta.role as string) || 'org_admin',
      authSource: 'supabase',
      account_id: (appMeta.account_id as string) || undefined,
      organization_id: (appMeta.organization_id as string) || undefined,
      establishment_id: (appMeta.establishment_id as string) || undefined,
      deployment_type: (appMeta.deployment_type as 'hosted' | 'dedicated') || 'dedicated',
      must_change_password: mustChangePassword,
      permissions: (appMeta.permissions as Record<string, Scope>) || {}
    };

    localStorage.setItem('sso_user', JSON.stringify(savUser));

    // Second facteur (port de Pimer) : c'est le SERVEUR qui dit s'il faut un defi
    // TOTP ou un enrolement (/api/auth/me repond en aal1 et porte mfa). Un ecran
    // decide par le navigateur seul ne protegerait rien : la garde est cote serveur.
    try {
      const reponse = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${session.access_token}` } });
      if (reponse.status === 401) {
        console.warn('[Auth] /api/auth/me : 401, deconnexion');
        handleLogout();
        return;
      }
      const corps = await reponse.json().catch(() => null);

      // Un compte TECHNICIEN n'a pas accès au site d'administration : le serveur le refuse
      // (403 ESPACE_RESERVE_ADMINISTRATEURS) en joignant le message à montrer et le lien de
      // l'application. La garde est côté serveur — ici, on explique et on ferme la session.
      // Ce test passe AVANT le refus générique ci-dessous, qui annoncerait sinon un second
      // facteur illisible pour un simple compte technicien.
      const refus = lireRefusTechnicien(reponse.status, corps);
      if (refus) {
        console.warn('[Auth] Compte technicien : site d\'administration refusé — session fermée');
        localStorage.removeItem('sso_token');
        localStorage.removeItem('sso_user');
        setSecondFacteur(null);
        setRefusTechnicien(refus);
        setAuthState({
          loading: false, authenticated: false, user: null, error: null, showLogin: true
        });
        // signOut() déclenche SIGNED_OUT -> handleLogout() : l'écran de refus survit parce que
        // handleLogout ne touche pas à refusTechnicien.
        supabase.auth.signOut().catch(() => {});
        return;
      }

      const mfa = corps?.mfa as { requis?: boolean; enrolementRequis?: boolean } | null | undefined;
      if (!reponse.ok) {
        const code = corps?.error?.code || `HTTP ${reponse.status}`;
        console.error('[Auth] Etat du second facteur illisible :', code);
        setAuthState({
          loading: false, authenticated: false, user: null, showLogin: false,
          error: 'Vérification du second facteur indisponible. Réessayez dans un instant.'
        });
        return;
      }
      if (mfa?.requis) {
        const { data: facteurs } = await supabase.auth.mfa.listFactors();
        const totp = (facteurs?.totp || []).find((f) => f.status === 'verified');
        console.log('[Auth] Second facteur requis : defi TOTP');
        setSecondFacteur({ ecran: 'defi', facteurId: totp?.id });
      } else if (mfa?.enrolementRequis) {
        console.log('[Auth] Second facteur a configurer : enrolement');
        setSecondFacteur({ ecran: 'enrolement' });
      } else {
        setSecondFacteur(null);
      }
    } catch (err) {
      console.error('[Auth] /api/auth/me injoignable :', err);
      setAuthState({
        loading: false, authenticated: false, user: null, showLogin: false,
        error: 'Serveur injoignable : impossible de vérifier le second facteur.'
      });
      return;
    }

    const permsCount = Object.keys(savUser.permissions || {}).length;
    console.log('[Auth] Supabase user:', savUser.email, '(role:', savUser.role, ', dossier:', savUser.dossier, ', perms:', permsCount, ', must_change_password:', mustChangePassword, ')');

    // Un compte autorisé lève un refus précédent : sinon, sa déconnexion plus tard ferait
    // réapparaître l'écran « Espace réservé aux administrateurs », qui ne dépend que de lui.
    setRefusTechnicien(null);

    setAuthState({
      loading: false,
      authenticated: true,
      user: savUser,
      error: null,
      showLogin: false
    });
  }

  /**
   * Appele apres un changement de mot de passe force reussi.
   * Refait initAuth() pour relire l'etat profil et lever le flag.
   */
  function handlePasswordChanged() {
    setAuthState(prev => prev.user
      ? { ...prev, user: { ...prev.user, must_change_password: false } }
      : prev
    );
  }

  /**
   * Login Supabase email/password
   */
  async function handleSupabaseLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);

    try {
      console.log('[Auth] Login Supabase:', loginEmail);

      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword
      });

      if (error) {
        console.error('[Auth] Erreur login Supabase:', error.message);
        setLoginError(
          error.message === 'Invalid login credentials'
            ? 'Email ou mot de passe incorrect'
            : error.message
        );
        return;
      }

      if (data.session) {
        console.log('[Auth] Login Supabase reussi');
        await handleSupabaseSession(data.session);
      }

    } catch (error) {
      console.error('[Auth] Erreur login:', error);
      setLoginError('Erreur de connexion au serveur');
    } finally {
      setLoginLoading(false);
    }
  }

  /**
   * Deconnexion
   */
  function handleLogout() {
    if (loggingOut.current) return;
    loggingOut.current = true;

    localStorage.removeItem('sso_token');
    localStorage.removeItem('sso_user');
    setSecondFacteur(null);

    // Deconnecter Supabase si connecte
    supabase.auth.signOut().catch(() => {});

    setAuthState({
      loading: false,
      authenticated: false,
      user: null,
      error: null,
      showLogin: true
    });

    setLoginEmail('');
    setLoginPassword('');
    setLoginError(null);

    console.log('[Auth] Deconnexion reussie');

    // Reset le guard apres un tick pour permettre un futur logout
    setTimeout(() => { loggingOut.current = false; }, 500);
  }

  // --- RENDU ---

  // Ecran « Definir mon mot de passe » (invitation) : AVANT le chargement, pour
  // qu'un invite arrivant par son lien ne voie ni le spinner ni le login. Une
  // session deja ouverte (autre compte) n'est pas concernee : l'email dit
  // d'ouvrir le lien en navigation privee.
  if (setPasswordToken && !authState.authenticated) {
    return (
      <SetPasswordPage
        token={setPasswordToken}
        onDone={() => {
          window.history.replaceState(null, '', '/');
          setSetPasswordToken('');
          setAuthState(prev => ({ ...prev, loading: false, showLogin: true }));
        }}
      />
    );
  }

  // Ecran de chargement
  if (authState.loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f1f5f9'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
          <p style={{ color: '#64748b', margin: 0 }}>Verification de l'authentification...</p>
        </div>
      </div>
    );
  }

  // Ecran « Espace réservé aux administrateurs » : le serveur a refusé un compte technicien.
  // Il remplace l'écran de connexion tant que le refus est posé — la carte et les styles sont
  // ceux de la connexion, pour que la personne reconnaisse le site qu'elle a ouvert.
  if (!authState.authenticated && refusTechnicien) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f1f5f9',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: isPhone ? 'calc(100vw - 24px)' : '420px',
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          padding: isPhone ? '24px' : '40px'
        }}>
          {/* Logo / Header — le même que l'écran de connexion */}
          <div style={{
            textAlign: 'center',
            marginBottom: '24px'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              backgroundColor: '#3b82f6',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="white" stroke="none">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
              </svg>
            </div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1e293b',
              margin: '0 0 4px'
            }}>
              ServUp
            </h1>
            <p style={{
              color: '#94a3b8',
              fontSize: '14px',
              margin: 0
            }}>
              Administration SAV
            </p>
          </div>

          <h2 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1e293b',
            textAlign: 'center',
            margin: '0 0 12px'
          }}>
            Espace réservé aux administrateurs
          </h2>

          <p style={{
            fontSize: '14px',
            lineHeight: '1.6',
            color: '#334155',
            margin: '0 0 24px'
          }}>
            {refusTechnicien.message}
          </p>

          {/* Le lien vient du serveur : le site ne connaît pas l'adresse de l'APK. */}
          {refusTechnicien.apkUrl && (
            <a
              href={refusTechnicien.apkUrl}
              style={{
                display: 'block',
                width: '100%',
                padding: '12px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                textAlign: 'center',
                textDecoration: 'none',
                boxSizing: 'border-box',
                transition: 'background-color 0.15s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; }}
            >
              📱 Télécharger l'application
            </a>
          )}

          <button
            type="button"
            onClick={() => setRefusTechnicien(null)}
            style={{
              width: '100%',
              marginTop: refusTechnicien.apkUrl ? '12px' : 0,
              padding: '12px',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '15px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
          >
            Retour à la connexion
          </button>

        </div>
      </div>
    );
  }

  // Ecran "Mot de passe oublie" (rendu alternatif au formulaire login)
  if (!authState.authenticated && authState.showLogin && showForgotPassword) {
    return (
      <ForgotPasswordPage onBackToLogin={() => setShowForgotPassword(false)} />
    );
  }

  // Formulaire de login (Supabase email/password)
  if (!authState.authenticated && authState.showLogin) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f1f5f9',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: isPhone ? 'calc(100vw - 24px)' : '420px',
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          padding: isPhone ? '24px' : '40px'
        }}>
          {/* Logo / Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '32px'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              backgroundColor: '#3b82f6',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="white" stroke="none">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
              </svg>
            </div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1e293b',
              margin: '0 0 4px'
            }}>
              ServUp
            </h1>
            <p style={{
              color: '#94a3b8',
              fontSize: '14px',
              margin: 0
            }}>
              Administration SAV
            </p>
          </div>

          {/* Message d'erreur de session */}
          {authState.error && (
            <div style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              fontSize: '13px',
              color: '#92400e'
            }}>
              {authState.error}
            </div>
          )}

          {/* Formulaire */}
          <form onSubmit={handleSupabaseLogin}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Email
              </label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="admin@exemple.com"
                required
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '15px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Mot de passe
              </label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="Votre mot de passe"
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '15px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>

            {/* Erreur de login */}
            {loginError && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                padding: '10px 12px',
                marginBottom: '16px',
                fontSize: '13px',
                color: '#991b1b',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#dc2626">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                {loginError}
              </div>
            )}

            <button
              type="submit"
              disabled={loginLoading}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: loginLoading ? '#93c5fd' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: loginLoading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.15s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => { if (!loginLoading) e.currentTarget.style.backgroundColor = '#2563eb'; }}
              onMouseLeave={(e) => { if (!loginLoading) e.currentTarget.style.backgroundColor = '#3b82f6'; }}
            >
              {loginLoading && (
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }}></div>
              )}
              {loginLoading ? 'Connexion...' : 'Se connecter'}
            </button>

            <div style={{ textAlign: 'center', marginTop: '14px' }}>
              <button
                type="button"
                onClick={() => { setLoginError(null); setShowForgotPassword(true); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  fontSize: '13px',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                Mot de passe oublié&nbsp;?
              </button>
            </div>
          </form>

        </div>
      </div>
    );
  }

  // Ecran d'erreur (sans formulaire login — cas improbable)
  if (!authState.authenticated) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f1f5f9',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: isPhone ? 'calc(100vw - 24px)' : '500px',
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          padding: isPhone ? '24px' : '40px'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#fee2e2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="#dc2626">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
          </div>

          <h1 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#1e293b',
            textAlign: 'center',
            marginBottom: '16px'
          }}>
            Authentification requise
          </h1>

          <p style={{
            color: '#64748b',
            textAlign: 'center',
            marginBottom: '24px',
            lineHeight: '1.5'
          }}>
            {authState.error || 'Une erreur inattendue est survenue.'}
          </p>

          <button
            onClick={() => window.location.reload()}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Recharger la page
          </button>
        </div>
      </div>
    );
  }

  // Double authentification imposee par le serveur : AVANT tout le reste, les
  // routes metier refusent un jeton aal1 (MFA_REQUIRED / MFA_ENROLLMENT_REQUIRED).
  if (authState.user && secondFacteur) {
    return (
      <SecondFacteurPage
        mode={secondFacteur.ecran}
        facteurId={secondFacteur.facteurId}
        email={authState.user.email || authState.user.username}
        onValide={async () => {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) await handleSupabaseSession(session);
        }}
        onLogout={handleLogout}
      />
    );
  }

  // Mot de passe a changer obligatoirement (1ere connexion ou reset OTP)
  if (authState.user?.must_change_password) {
    return (
      <ChangePasswordRequiredPage
        authUserId={authState.user.id}
        email={authState.user.email || authState.user.username}
        onSuccess={handlePasswordChanged}
        onLogout={handleLogout}
      />
    );
  }

  // Utilisateur authentifie
  return (
    <AuthContext.Provider value={{
      user: authState.user,
      logout: handleLogout,
      supabase
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export default SupabaseWrapper;
